/**
 * Shared helpers for working with the pricing catalog returned by
 * `getPricingList`. Everything here is defensive: the API grows new
 * entries (and new sections) over time, so nothing may assume a fixed
 * shape, a fixed ordering or even that a price exists at all.
 */

export const HOURS_PER_MONTH = 730;
export const MONTHS_PER_YEAR = 12;

/** Entries priced individually (GPU, dedicated hosts, EFS, private connect). */
export const isOnRequest = (entry) =>
  !entry || entry.price === null || entry.price === undefined;

export const priceOf = (entry) => (isOnRequest(entry) ? 0 : entry.price);

/** Look an entry up by its `type` instead of by array index. */
export const byType = (list, type) =>
  (Array.isArray(list) ? list : []).find((entry) => entry.type === type);

export const priceByType = (list, type) => priceOf(byType(list, type));

const num = (match) => (match ? Number(match[1]) : null);

/**
 * Parse a free-form `spec` string such as
 *   "16vCPUs, 64GB RAM, 640GB NVMe Storage, SEV-SNP CVM activated"
 *   "dd1.large (4vCPUs, 16GB RAM)"
 *   "2vCPUs, 8GB RAM, maximal 7 days backup history, 1 node"
 * Missing values come back as `null` rather than throwing -- on-request
 * entries ship an empty spec.
 */
export const parseSpec = (spec) => {
  const raw = typeof spec === "string" ? spec : "";

  return {
    raw,
    vCPUs: num(raw.match(/(\d+(?:\.\d+)?)\s*vCPUs?/i)),
    memory: num(raw.match(/(\d+(?:\.\d+)?)\s*Gi?B\s*RAM/i)),
    storage: num(raw.match(/(\d+(?:\.\d+)?)\s*GB\s*(?:NVMe\s*)?Storage/i)),
    nodes: num(raw.match(/(\d+)\s*nodes?/i)),
    backupDays: num(raw.match(/(\d+)\s*days?\s*backup/i)),
    confidential: /SEV-SNP/i.test(raw),
  };
};

export const COMPUTE_FAMILIES = [
  {
    id: "dd1",
    label: "DD1",
    caption: "General purpose",
    match: (type) => type.startsWith("dd1."),
  },
  {
    id: "dd2",
    label: "DD2",
    caption: "General purpose, gen2 hardware",
    match: (type) => type.startsWith("dd2.") && !/Cvm$/.test(type),
  },
  {
    id: "dd2-cvm",
    label: "DD2 Confidential",
    caption: "SEV-SNP confidential computing",
    match: (type) => type.startsWith("dd2.") && /Cvm$/.test(type),
  },
  {
    id: "gpu",
    label: "GPU",
    caption: "Accelerated computing",
    match: (type) => type.startsWith("gpu."),
  },
  {
    id: "dedicated",
    label: "Dedicated Host",
    caption: "Single-tenant hardware",
    match: (type) => type.startsWith("dedicatedHost."),
  },
];

export const familyOf = (type) => {
  const name = typeof type === "string" ? type : "";
  const family = COMPUTE_FAMILIES.find((candidate) => candidate.match(name));
  return family ? family.id : "other";
};

/**
 * Decorate raw compute entries with parsed specs and family metadata.
 * Never throws on entries without a spec.
 */
export const normalizeCompute = (list) =>
  (Array.isArray(list) ? list : []).map((entry) => {
    const spec = parseSpec(entry.spec);
    return {
      ...entry,
      spec: entry.spec || "",
      specs: spec,
      family: familyOf(entry.type),
      confidential: spec.confidential,
      onRequest: isOnRequest(entry),
      hourlyPrice: isOnRequest(entry) ? null : entry.price / HOURS_PER_MONTH,
    };
  });

/** A single priced row inside an estimate line ("show calculations"). */
export const lineItem = ({
  label,
  detail = "",
  quantity = 1,
  unit = "",
  unitPrice = 0,
  cost,
}) => ({
  label,
  detail,
  quantity,
  unit,
  unitPrice,
  cost: cost === undefined ? quantity * unitPrice : cost,
});

export const sumItems = (items) =>
  (items || []).reduce((total, item) => total + (item.cost || 0), 0);

/**
 * Build the estimate line every configuration component emits. Keeping one
 * shape everywhere is what lets the summary render quantities, per-item
 * calculations and 12-month totals generically.
 */
export const estimateLine = ({
  key,
  serviceId,
  name,
  description = "",
  config = {},
  items = [],
  quantity = 1,
  onRequest = false,
  dynamic = null,
  meta = {},
}) => {
  const unitMonthlyCost = sumItems(items);
  const monthlyCost = unitMonthlyCost * quantity;

  return {
    key,
    serviceId,
    name,
    description,
    config,
    items,
    quantity,
    onRequest,
    dynamic,
    meta,
    unitMonthlyCost,
    monthlyCost,
    totalCost: monthlyCost * MONTHS_PER_YEAR,
  };
};

/** Round for display without dragging floating point noise into totals. */
export const round = (value, digits = 2) => {
  const factor = 10 ** digits;
  return Math.round((value + Number.EPSILON) * factor) / factor;
};
